import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowUpRight, Loader2 } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { api } from "@/lib/api";

const socials = [
  { label: "[untitled] stream", href: "https://untitled.stream", handle: "untitled.stream" },
  { label: "Email", href: "mailto:contact@ocean-music.com", handle: "contact@ocean-music.com" },
  { label: "Instagram", href: "#", handle: "@ocean" },
  { label: "Bookings", href: "mailto:booking@ocean-music.com", handle: "booking@ocean-music.com" },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "General", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Received. If it matters, you will hear back.");
      setForm({ name: "", email: "", subject: "General", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-white/20 py-4 font-mono text-sm text-[#f2f0ec] placeholder:text-[#555] focus:border-[#f2f0ec] focus:outline-none transition-colors";

  return (
    <div data-testid="contact-page" className="min-h-screen">
      <PageHeader
        chapter="The Archive · Contact"
        title="Contact"
        intro="Collaboration, licensing, bookings. Or tell me which world you got lost in."
      />
      <div className="px-6 md:px-12 pb-40 max-w-[1500px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
        {/* Form */}
        <form data-testid="contact-form" onSubmit={submit} className="md:col-span-7 space-y-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
            <input data-testid="contact-name" className={inputCls} placeholder="NAME" value={form.name} onChange={update("name")} />
            <input data-testid="contact-email" type="email" className={inputCls} placeholder="EMAIL" value={form.email} onChange={update("email")} />
          </div>
          <input data-testid="contact-subject" className={inputCls} placeholder="SUBJECT" value={form.subject} onChange={update("subject")} />
          <textarea data-testid="contact-message" rows={5} className={`${inputCls} resize-none`} placeholder="MESSAGE" value={form.message} onChange={update("message")} />
          <button
            type="submit"
            data-testid="contact-submit"
            disabled={loading}
            className="group inline-flex items-center gap-3 border border-white/20 px-9 py-4 font-mono text-[11px] uppercase tracking-[0.3em] text-[#f2f0ec] hover:bg-[#f2f0ec] hover:text-black transition-all duration-500 disabled:opacity-50"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : null}
            {loading ? "Sending" : "Send Message"}
            {!loading && <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />}
          </button>
        </form>

        {/* Socials bento */}
        <div className="md:col-span-4 md:col-start-9 grid grid-cols-1 gap-px bg-white/10 border border-white/10 self-start">
          {socials.map((s, i) => (
            <motion.a
              key={i}
              href={s.href}
              target={s.href.startsWith("http") ? "_blank" : undefined}
              rel="noreferrer"
              data-testid={`social-${s.label.toLowerCase().replace(/\W+/g, "-")}`}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.06 }}
              className="group bg-[#080808] p-7 hover:bg-[#0e0e0e] transition-colors flex items-center justify-between"
            >
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#8a8a8a] mb-2">{s.label}</p>
                <p className="font-serif text-2xl text-[#f2f0ec]">{s.handle}</p>
              </div>
              <ArrowUpRight size={18} className="text-[#555] group-hover:text-[#f2f0ec] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" />
            </motion.a>
          ))}
        </div>
      </div>
    </div>
  );
}
